import { useEffect, useRef } from 'react';
import { installOfficeDragDropHandler } from '@/services/office/dragDrop';
import { dropTargetAtPoint, type DropTarget } from '@/services/office/dropHitTest';
import { officeReady } from '@/services/office/officeReady';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { useDropFiler } from '@/features/file-mail/useDropFiler';
import { useMailDropStore } from '@/stores/mailDropStore';

/**
 * Installs the Office.js DragAndDropEvent handler (Outlook Web / new Outlook;
 * Classic Desktop / Mac use HTML5 DnD via DropZone / FolderRow).
 *
 * The event exposes `taskPaneX/taskPaneY`, so on each dragover we hit-test the
 * drop target under the cursor (a folder, a project root, or nothing) and drive
 * the store: the hovered folder / project root lights up, and nothing lights up
 * over blank space (so we never suggest a drop that would go nowhere). On drop
 * we file into that target; `getFallback` supplies a whole-view target only on
 * the folder-detail page (the current folder/root) — on the home tree it
 * returns null, so a drop that misses every target is a no-op. The Office event
 * never raises a dragleave, so a short auto-expiring timer (refreshed on every
 * dragover tick) clears the highlight once the drag ends.
 */
export function useOfficeDragDrop(getFallback: () => DropTarget | null): void {
  const { fileMails } = useDropFiler();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const clearTimer = () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
        hideTimer.current = undefined;
      }
    };
    // Wait for Office to be ready before touching Office.EventType /
    // mailbox.addHandlerAsync — the app now mounts before Office.onReady.
    void officeReady().then(() => {
      installOfficeDragDropHandler({
        onDragOver: (pos) => {
          // Raw hit-test (no fallback) so blank space shows no highlight.
          const target = pos ? dropTargetAtPoint(pos.x, pos.y) : null;
          useMailDropStore.getState().setDrag(target);
          clearTimer();
          hideTimer.current = setTimeout(
            () => useMailDropStore.getState().endDrag(),
            400,
          );
        },
        onDrop: async (mails, pos) => {
          clearTimer();
          const hit = pos ? dropTargetAtPoint(pos.x, pos.y) : null;
          const target = hit ?? getFallback();
          useMailDropStore.getState().endDrag();
          addBreadcrumb({
            category: 'office.event',
            message: 'office-drop',
            data: { count: mails.length, kind: target?.kind ?? 'none', hit: Boolean(hit) },
          });
          await fileMails(mails, target);
        },
      });
    });
    return clearTimer;
  }, [getFallback, fileMails]);
}
